/**
 * FindDocument — Brief 91 SharePoint document finder page (/documents/find).
 *
 * Phrase search across the firm's SharePoint matter folders via
 * POST /api/documents/find, with matter / date / type / uploader refinements,
 * a sort dropdown, infinite-scroll pagination, inline preview drawer and
 * open-in-SharePoint / download actions. Authz is enforced server-side.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FileSearch, Loader2, AlertTriangle } from "lucide-react";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
import DocumentSearchPanel from "../../components/documents/DocumentSearchPanel";
import DocumentResultCard from "../../components/documents/DocumentResultCard";
import DocumentPreviewDrawer from "../../components/documents/DocumentPreviewDrawer";
import FolderTreeSidebar from "../../components/documents/FolderTreeSidebar";
import BundleBasket from "../../components/documents/BundleBasket";
import BundleUploadPanel from "../../components/documents/BundleUploadPanel";
import BundleDrafts from "../../components/documents/BundleDrafts";
import { BUNDLE_BUILDER_ENABLED } from "../../config/bundleFlag";
import * as bundleApi from "../../api/bundleApi";

// BM-H253 — the FE-side default draft name that pre-populates the save prompt.
// Mirrors the server-side fallback in document_bundle.save_draft so the user
// can accept it with Enter, edit inline, or clear and let the server default.
// Uses an explicit 3-letter month array so the format is deterministic across
// Node/ICU versions (some emit "Sept" rather than "Sep" from toLocaleString).
const DRAFT_MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function defaultDraftName(matterRef) {
  const d = new Date();
  const day = String(d.getDate()).padStart(2, "0");
  const month = DRAFT_MONTHS_SHORT[d.getMonth()];
  const year = d.getFullYear();
  const stamp = `${day}-${month}-${year}`;
  const ref = (matterRef || "").trim();
  return ref ? `${ref} bundle ${stamp}` : `Bundle ${stamp}`;
}

const SORT_OPTIONS = [
  { value: "default", label: "Best match (exact matter first, then newest)" },
  { value: "relevance", label: "Relevance" },
  { value: "modified_desc", label: "Date modified (newest first)" },
  { value: "modified_asc", label: "Date modified (oldest first)" },
  { value: "name_asc", label: "Name (A–Z)" },
  { value: "name_desc", label: "Name (Z–A)" },
  { value: "type_asc", label: "Type (A–Z)" },
  { value: "type_desc", label: "Type (Z–A)" },
  { value: "matter_asc", label: "Matter ref (A–Z)" },
  { value: "matter_desc", label: "Matter ref (Z–A)" },
];

export default function FindDocument() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(null);   // last submitted filter object
  const [sort, setSort] = useState("default");
  const [results, setResults] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [total, setTotal] = useState(0);
  // Search totals are Graph's own estimate; a folder listing is an exact count.
  const [totalIsEstimate, setTotalIsEstimate] = useState(false);
  const [scopedToMatter, setScopedToMatter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [activeFolder, setActiveFolder] = useState(null);  // folder being browsed, if any
  const [basket, setBasket] = useState({ items: [], count: 0 });
  const [basketBusy, setBasketBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const sentinelRef = useRef(null);

  // Load the persistent server-side basket once when the feature is enabled,
  // so it survives a page refresh and searches across matters.
  useEffect(() => {
    if (!BUNDLE_BUILDER_ENABLED) return;
    bundleApi.getBasket().then(setBasket).catch(() => {});
  }, []);

  // BM-H254 — keep the drafts list in sync with the server.
  const refreshDrafts = useCallback(async () => {
    if (!BUNDLE_BUILDER_ENABLED) return;
    try {
      const data = await bundleApi.listDrafts();
      setDrafts(data?.drafts || []);
    } catch {
      // No drafts is not an error; a real server error surfaces on save/open.
    }
  }, []);

  useEffect(() => { refreshDrafts(); }, [refreshDrafts]);

  const bundledIds = new Set((basket.items || []).map((it) => it.source_item_id));

  const runBasketOp = useCallback(async (fn) => {
    setBasketBusy(true);
    try {
      const data = await fn();
      if (data) setBasket(data);
      return data;
    } catch (err) {
      setError(errorMessage(err, "Bundle action failed."));
      return null;
    } finally {
      setBasketBusy(false);
    }
  }, []);

  const handleAddToBundle = useCallback((doc) => runBasketOp(() =>
    bundleApi.addToBasket({
      item_id: doc.id,
      filename: doc.title,
      file_type: doc.type,
      matter_ref: doc.matter_ref,
    })
  ), [runBasketOp]);

  const handleSaveDraft = useCallback(async () => {
    // BM-H253 — pre-populate the prompt with the server's own default naming
    // convention so a user can accept with Enter without inventing a name.
    // basket.matter_ref (from an opened draft) wins, then the top item's
    // source_matter_ref, else "Bundle <DATE>".
    const suggestedMatter =
      basket?.matter_ref
      || basket?.items?.[0]?.source_matter_ref
      || "";
    const suggested = defaultDraftName(suggestedMatter);
    const name = window.prompt("Name this bundle draft:", suggested);
    if (name === null) return;
    const trimmed = name.trim();
    const data = await runBasketOp(() =>
      bundleApi.saveDraft({ name: trimmed || undefined })
    );
    if (data) refreshDrafts();
  }, [basket, runBasketOp, refreshDrafts]);

  // BM-H254 — open a named draft into the active basket, then refresh both.
  const handleOpenDraft = useCallback(async (draftId) => {
    const data = await runBasketOp(() => bundleApi.openDraft(draftId));
    if (data) refreshDrafts();
  }, [runBasketOp, refreshDrafts]);

  // BM-H255 — delete a named draft after confirmation, then refresh the list.
  const handleDeleteDraft = useCallback(async (draft) => {
    const label = draft?.name || "this draft";
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete draft "${label}"? This cannot be undone.`)) return;
    setBasketBusy(true);
    try {
      await bundleApi.deleteDraft(draft.draft_id);
      await refreshDrafts();
    } catch (err) {
      setError(errorMessage(err, "Delete draft failed."));
    } finally {
      setBasketBusy(false);
    }
  }, [refreshDrafts]);

  const runSearch = useCallback(async (activeFilters, activeSort, pageToken) => {
    const isMore = !!pageToken;
    if (isMore) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/documents/find`, {
        ...activeFilters,
        sort: activeSort,
        page_token: pageToken || null,
      });
      const data = res.data || {};
      setResults((prev) => (isMore ? [...prev, ...(data.results || [])] : (data.results || [])));
      setNextPageToken(data.next_page_token || null);
      setTotal(data.total_estimate || 0);
      setTotalIsEstimate(data.total_is_estimate !== false);
      setScopedToMatter(!!data.scoped_to_matter_folder);
    } catch (err) {
      // Brief 97 — backend now returns structured 500 detail
      // ({error, message, trace, endpoint}) via the trace wrapper. Surface
      // the message cleanly; keep the trace in console for debugging without
      // bleeding it into the UI.
      const rawDetail = err?.response?.data?.detail;
      let msg;
      if (typeof rawDetail === "string") {
        msg = rawDetail;
      } else if (rawDetail && typeof rawDetail === "object") {
        msg = `${rawDetail.error || "Error"}: ${rawDetail.message || "Unknown error"}`;
        if (Array.isArray(rawDetail.trace)) {
          // eslint-disable-next-line no-console
          console.error("[FindDocument] backend trace:", rawDetail.trace.join("\n"));
        }
      } else {
        msg = "Search failed. Please try again.";
      }
      setError(msg);
      if (!isMore) { setResults([]); setNextPageToken(null); setTotal(0); }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Folder browsing: load a SharePoint folder's contents into the same results
  // list the search path feeds, so every per-document action (preview,
  // multi-select, add-to-basket) works identically on folder-browsed docs.
  const runFolderBrowse = useCallback(async (folder, activeSort) => {
    setLoading(true);
    setError(null);
    setNextPageToken(null);
    try {
      const res = await axios.post(`${API}/documents/find`, {
        folder_id: folder.id,
        sort: activeSort,
      });
      const data = res.data || {};
      setResults(data.results || []);
      setTotal(data.total_estimate || 0);
      setTotalIsEstimate(false);
      setScopedToMatter(false);
      setNextPageToken(null);
    } catch (err) {
      setError(errorMessage(err, "Couldn't open that folder. Please try again."));
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelectFolder = useCallback((folder) => {
    // Browsing a folder replaces any active phrase search.
    setFilters(null);
    setActiveFolder(folder);
    setSearched(true);
    runFolderBrowse(folder, sort);
  }, [runFolderBrowse, sort]);

  const handleSearch = useCallback((newFilters) => {
    setActiveFolder(null);
    setFilters(newFilters);
    setSearched(true);
    runSearch(newFilters, sort, null);
  }, [runSearch, sort]);

  const handleClear = useCallback(() => {
    setFilters(null);
    setActiveFolder(null);
    setResults([]);
    setNextPageToken(null);
    setTotal(0);
    setSearched(false);
    setError(null);
  }, []);

  const handleSortChange = useCallback((newSort) => {
    setSort(newSort);
    if (activeFolder) runFolderBrowse(activeFolder, newSort);
    else if (filters) runSearch(filters, newSort, null);
  }, [activeFolder, filters, runFolderBrowse, runSearch]);

  const handleOpenMatter = useCallback((doc) => {
    if (doc?.matter_ref) navigate(`/matter/${encodeURIComponent(doc.matter_ref)}`);
  }, [navigate]);

  const previewIndex = previewDoc ? results.findIndex((r) => r.id === previewDoc.id) : -1;

  // Infinite scroll: load the next page when the sentinel enters view.
  useEffect(() => {
    if (!nextPageToken || loading || loadingMore) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && filters) {
        runSearch(filters, sort, nextPageToken);
      }
    }, { rootMargin: "200px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [nextPageToken, loading, loadingMore, filters, sort, runSearch]);

  return (
    <div className={`p-6 mx-auto ${BUNDLE_BUILDER_ENABLED ? "max-w-7xl" : "max-w-5xl"}`} data-testid="find-document-page">
      <div className="flex items-center gap-2 mb-4">
        <FileSearch className="text-teal" size={22} />
        <h1 className="text-lg font-semibold text-white">Find document</h1>
      </div>

      <DocumentSearchPanel onSearch={handleSearch} onClear={handleClear} busy={loading} />

      <div className={BUNDLE_BUILDER_ENABLED ? "flex flex-col lg:flex-row gap-6 mt-2" : ""}>
        {BUNDLE_BUILDER_ENABLED && (
          <FolderTreeSidebar
            selectedFolderId={activeFolder?.id || null}
            onSelectFolder={handleSelectFolder}
          />
        )}
        <div className="flex-1 min-w-0">
      {/* Results header */}
      {searched && (
        <div className="flex items-center justify-between mt-6 mb-3">
          <div className="text-sm text-slate-400" data-testid="doc-result-count">
            {loading
              ? (activeFolder ? "Opening folder…" : "Searching…")
              : (
                <>
                  {activeFolder && (
                    <span className="text-teal font-medium">{activeFolder.name}: </span>
                  )}
                  {activeFolder
                    ? `${total} item${total === 1 ? "" : "s"}`
                    : `${totalIsEstimate && total > 0 ? "About " : ""}${total} result${total === 1 ? "" : "s"}`}
                  {!activeFolder && scopedToMatter && filters?.matter_ref && (
                    <span data-testid="doc-scope-note">
                      {" "}in {filters.matter_ref}&apos;s SharePoint folder and its subfolders
                    </span>
                  )}
                </>
              )}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-400">Sort</label>
            <select
              data-testid="doc-sort-select"
              value={sort}
              onChange={(e) => handleSortChange(e.target.value)}
              className="rounded-md bg-navy-hover border border-navy-border text-white text-xs px-2 py-1.5 focus:border-teal focus:ring-teal"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-amber-700/50 bg-amber-900/30 px-3 py-2 text-sm text-amber-200 mb-3" data-testid="doc-search-error">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="animate-spin mr-2" size={18} /> Searching…
        </div>
      )}

      {/* The filters emptied this stretch of results but SharePoint has more. */}
      {searched && !loading && !error && results.length === 0 && nextPageToken && (
        <div className="text-center py-12 text-slate-400" data-testid="doc-search-further">
          <p className="text-sm">Nothing matching yet in the first results SharePoint returned.</p>
          <button
            type="button"
            onClick={() => runSearch(filters, sort, nextPageToken)}
            disabled={loadingMore}
            className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs bg-slate-800 text-slate-200 border border-navy-border hover:bg-slate-700"
          >
            {loadingMore ? <Loader2 className="animate-spin" size={13} /> : <FileSearch size={13} />} Search further
          </button>
        </div>
      )}

      {/* Empty state */}
      {searched && !loading && !error && results.length === 0 && !nextPageToken && (
        <div className="text-center py-12 text-slate-400" data-testid="doc-empty-state">
          <FileSearch className="mx-auto mb-3 opacity-40" size={32} />
          {activeFolder ? (
            <>
              <p className="text-sm">
                No documents in{" "}
                <span className="text-white font-medium">{activeFolder.name}</span>.
              </p>
              <p className="text-xs mt-1">Pick another folder or run a search.</p>
            </>
          ) : (
            <>
              <p className="text-sm">
                No documents found for{" "}
                <span className="text-white font-medium">“{filters?.phrase}”</span>.
              </p>
              <p className="text-xs mt-1">Try a different phrase or relax your filters.</p>
            </>
          )}
        </div>
      )}

      {/* Results list */}
      {!loading && results.length > 0 && (
        <div className="space-y-3">
          {results.map((doc) => (
            <DocumentResultCard
              key={doc.id}
              doc={doc}
              onPreview={setPreviewDoc}
              onOpenMatter={handleOpenMatter}
              onAddToBundle={BUNDLE_BUILDER_ENABLED ? handleAddToBundle : undefined}
              inBundle={bundledIds.has(doc.id)}
            />
          ))}
          <div ref={sentinelRef} />
          {loadingMore && (
            <div className="flex items-center justify-center py-4 text-slate-400">
              <Loader2 className="animate-spin mr-2" size={16} /> Loading more…
            </div>
          )}
        </div>
      )}
        </div>

        {BUNDLE_BUILDER_ENABLED && (
          <div className="w-full lg:w-80 shrink-0 space-y-4 lg:sticky lg:top-6 self-start">
            <BundleBasket
              items={basket.items}
              busy={basketBusy}
              onMove={(id, dir) => runBasketOp(() => bundleApi.moveItem(id, dir))}
              onRemove={(id) => runBasketOp(() => bundleApi.removeItem(id))}
              onRelabel={(id, label) => runBasketOp(() => bundleApi.relabelItem(id, label))}
              onReorder={(order) => runBasketOp(() => bundleApi.reorderBasket(order))}
              onClear={() => runBasketOp(() => bundleApi.clearBasket())}
              onSaveDraft={handleSaveDraft}
              onDownloadZip={() => bundleApi.downloadZip().catch(() => setError("Zip download failed."))}
              onExportPdf={() => bundleApi.downloadBundlePdf().catch(() => setError("Bundle export failed."))}
            />
            <BundleDrafts
              drafts={drafts}
              busy={basketBusy}
              onOpen={handleOpenDraft}
              onDelete={handleDeleteDraft}
            />
            <BundleUploadPanel matterRef={filters?.matter_ref || ""} />
          </div>
        )}
      </div>

      <DocumentPreviewDrawer
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
        onPrev={previewIndex > 0 ? () => setPreviewDoc(results[previewIndex - 1]) : undefined}
        onNext={previewIndex >= 0 && previewIndex < results.length - 1
          ? () => setPreviewDoc(results[previewIndex + 1]) : undefined}
        position={previewIndex >= 0 ? `${previewIndex + 1} of ${results.length}` : undefined}
        onAddToBundle={BUNDLE_BUILDER_ENABLED ? handleAddToBundle : undefined}
        inBundle={previewDoc ? bundledIds.has(previewDoc.id) : false}
      />
    </div>
  );
}
